/** @jsxImportSource @emotion/react */
import { StatisticsDateRangePicker } from '../components/StatisticsDateRangePicker';
import { useStatisticsDateRange } from '../hooks/useStatisticsDateRange';
import { css } from '@emotion/react';
import React, { useState, useEffect, useCallback } from 'react';
import { message, Card, Typography, Select, Space } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { AccessFilterHeader, AccessSubTab, AccessPeriod } from './components/AccessFilterHeader';
import { AccessSubTabs } from './components/AccessSubTabs';
import { AccessOverviewCards } from './components/AccessOverviewCards';
import {
  FeatureDoughnutChartCard,
  TrendLineChartCard,
} from './components/AccessOverviewCharts';
import {
  getAccessOverviewData,
  exportAccessOverviewExcel,
  AccessOverviewResponse,
} from '../services/statistics.service';
import { captureAccessOverview } from './utils/captureAccessOverview';
import { StatisticsAccessUserPage } from './pages/StatisticsAccessUserPage';
import { StatisticsAccessFrequencyPage } from './pages/StatisticsAccessFrequencyPage';
import { StatisticsAccessAvgTimePage } from './pages/StatisticsAccessAvgTimePage';

const { Title } = Typography;

interface StatisticsAccessPageProps {
  activeMainTab: 'profile' | 'access';
  onMainTabChange: (val: 'profile' | 'access') => void;
}

// Dropdown options for overview time filter per Figma Image 3
const OVERVIEW_TIME_OPTIONS = [
  { value: 'today', label: 'Hôm nay' },
  { value: 'this_month', label: 'Tháng này' },
  { value: 'this_year', label: 'Năm này' },
  { value: 'last_30_days', label: '30 ngày gần nhất' },
  { value: 'last_12_months', label: '12 tháng gần nhất' },
  { value: 'last_3_years', label: '3 năm gần nhất' },
];

export const StatisticsAccessPage: React.FC<StatisticsAccessPageProps> = ({
  activeMainTab,
  onMainTabChange,
}) => {
  const [subTab, setSubTab] = useState<AccessSubTab>('overview');
  const [selectedPeriod, setSelectedPeriod] = useState<AccessPeriod | undefined>(undefined);
  const { period, dateRange, changePeriod: setPeriod,
    applyDateRange: applyRange } = useStatisticsDateRange();
  const overviewVersion = React.useRef(0);
  const [childExportReady, setChildExportReady] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [data, setData] = useState<AccessOverviewResponse | null>(null);

  const overviewContentRef = React.useRef<HTMLDivElement>(null);
  const userPageRef = React.useRef<any>(null);
  const freqPageRef = React.useRef<any>(null);
  const avgTimePageRef = React.useRef<any>(null);

  const fetchData = useCallback(async () => {
    if (subTab !== 'overview') return;
    const version = ++overviewVersion.current;
    setLoadFailed(false);
    setLoading(true);
    try {
      const res = await getAccessOverviewData({
        period: period as AccessPeriod,
        from_date: dateRange[0] || undefined,
        to_date: dateRange[1] || undefined,
      });
      if (version !== overviewVersion.current) return;
      setData(res);
    } catch (err: any) {
      if (version !== overviewVersion.current) return;
      setLoadFailed(true);
      console.error('Fetch access overview error:', err);
      message.error('Không thể tải dữ liệu thống kê lượt truy cập');
    } finally {
      if (version === overviewVersion.current) setLoading(false);
    }
  }, [period, dateRange, subTab]);

  useEffect(() => {
    fetchData();
    return () => { overviewVersion.current += 1; };
  }, [fetchData]);

  const handleSubTabChange = (val: AccessSubTab) => {
    setChildExportReady(false);
    setSubTab(val);
  };

  const handleTimeSelectChange = (val?: AccessPeriod) => {
    setSelectedPeriod(val);
    setPeriod(val || 'all');
  };
  const applyDateRange = (range: [string | null, string | null]) => {
    setSelectedPeriod(undefined);
    applyRange(range);
  };

  const handleExportExcel = async () => {
    if (subTab === 'overview' ? loading || loadFailed : !childExportReady) return;
    setExportLoading(true);
    try {
      if (subTab === 'overview') {
        if (!overviewContentRef.current) return;
        const canvas = await captureAccessOverview(overviewContentRef.current);
        const imgBase64 = canvas.toDataURL('image/png');

        let timeRangeLabel = 'Tất cả';
        if (period === 'today') {
          timeRangeLabel = `${dayjs().format('DD/MM/YYYY')} - ${dayjs().format('DD/MM/YYYY')}`;
        } else if (period === 'this_month') {
          const range = data?.resolved_date_range;
          const end = range?.to_date ? dayjs(range.to_date) : dayjs();
          const start = range?.from_date ? dayjs(range.from_date) : end.startOf('month');
          timeRangeLabel = `${start.format('DD/MM/YYYY')} - ${end.format('DD/MM/YYYY')}`;
        } else if (period === 'this_year') {
          timeRangeLabel = `${dayjs().startOf('year').format('DD/MM/YYYY')} - ${dayjs().endOf('year').format('DD/MM/YYYY')}`;
        } else if (period === 'last_30_days') {
          const range = data?.resolved_date_range;
          const end = range?.to_date ? dayjs(range.to_date) : dayjs();
          const start = range?.from_date ? dayjs(range.from_date) : end.subtract(29, 'day');
          timeRangeLabel = `${start.format('DD/MM/YYYY')} - ${end.format('DD/MM/YYYY')}`;
        } else if (period === 'last_12_months') {
          const range = data?.resolved_date_range;
          const end = range?.to_date ? dayjs(range.to_date) : dayjs();
          const start = range?.from_date ? dayjs(range.from_date) : end.subtract(11, 'month').startOf('month');
          timeRangeLabel = `${start.format('DD/MM/YYYY')} - ${end.format('DD/MM/YYYY')}`;
        } else if (period === 'last_3_years') {
          const range = data?.resolved_date_range;
          const end = range?.to_date ? dayjs(range.to_date) : dayjs();
          const start = range?.from_date ? dayjs(range.from_date) : end.subtract(2, 'year').startOf('year');
          timeRangeLabel = `${start.format('DD/MM/YYYY')} - ${end.format('DD/MM/YYYY')}`;
        } else if (period === 'custom' && (dateRange[0] || dateRange[1])) {
          timeRangeLabel = `${dateRange[0] ? dayjs(dateRange[0]).format('DD/MM/YYYY') : '...'} - ${dateRange[1] ? dayjs(dateRange[1]).format('DD/MM/YYYY') : '...'}`;
        }

        const blob = await exportAccessOverviewExcel(imgBase64, timeRangeLabel);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `tong_quan_luot_truy_cap_${Date.now()}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      } else if (subTab === 'users' && userPageRef.current) {
        await userPageRef.current.handleExport();
      } else if (subTab === 'frequency' && freqPageRef.current) {
        await freqPageRef.current.handleExport();
      } else if (subTab === 'avg_time' && avgTimePageRef.current) {
        await avgTimePageRef.current.handleExport();
      }
    } catch (err: any) {
      console.error('Export overview excel error:', err);
      message.error('Xuất báo cáo Excel thất bại');
    } finally {
      setExportLoading(false);
    }
  };

  const subTabsNode = (
    <AccessSubTabs subTab={subTab} onSubTabChange={handleSubTabChange} />
  );

  return (
    <div css={pageContainerStyle}>
      <AccessFilterHeader
        activeMainTab={activeMainTab}
        onMainTabChange={onMainTabChange}
        onExportExcel={handleExportExcel}
        exportLoading={exportLoading}
        exportDisabled={subTab === 'overview' ? loading || loadFailed : !childExportReady}
      />

      {subTab === 'overview' && (
        <Card css={overviewMainCardStyle}>
          {subTabsNode}

          {/* Keep the overview title above the time filters as in the design. */}
          <div css={overviewHeaderStyle}>
            <Title level={4} style={{ margin: 0, fontSize: 16 }}>
              Tổng quan Lượt truy cập
            </Title>
            <div css={overviewFilterRowStyle}>
              <Space size={12} wrap>
                <Select
                  placeholder="Chọn thời gian"
                  allowClear
                  value={selectedPeriod}
                  onChange={handleTimeSelectChange}
                  style={{ width: 180 }}
                  className={selectedPeriod ? 'bold-select-value' : ''}
                  suffixIcon={<DownOutlined style={{ color: '#595959' }} />}
                  options={OVERVIEW_TIME_OPTIONS}
                />
                <StatisticsDateRangePicker value={dateRange} onChange={applyDateRange}
                  showApply />
              </Space>
            </div>
          </div>

          {/* Capture the same responsive layout displayed on the web page. */}
          <div ref={overviewContentRef} css={overviewContentStyle}>
            <div css={overviewTopRowStyle}>
              <AccessOverviewCards loading={loading} period={period as AccessPeriod} cards={data?.cards} />
              <FeatureDoughnutChartCard
                loading={loading}
                totalAccess={data?.cards?.totalAccess}
                featureChartData={data?.featureChartData}
              />
            </div>
            <div css={overviewTrendRowStyle}>
              <TrendLineChartCard
                loading={loading}
                period={period}
                trendChartData={data?.trendChartData}
              />
            </div>
          </div>
        </Card>
      )}

      {subTab === 'users' && (
        <StatisticsAccessUserPage ref={userPageRef} subTabs={subTabsNode} onExportAvailabilityChange={setChildExportReady} />
      )}

      {subTab === 'frequency' && (
        <StatisticsAccessFrequencyPage ref={freqPageRef} subTabs={subTabsNode} onExportAvailabilityChange={setChildExportReady} />
      )}

      {subTab === 'avg_time' && (
        <StatisticsAccessAvgTimePage ref={avgTimePageRef} subTabs={subTabsNode} onExportAvailabilityChange={setChildExportReady} />
      )}
    </div>
  );
};

export default StatisticsAccessPage;

const pageContainerStyle = css`
  background: var(--gray-soft-color);
  padding: 1.4rem 2.4rem;
  min-height: calc(100vh - var(--header-height));
`;

const overviewMainCardStyle = css`
  margin-bottom: 16px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  background: #ffffff;

  > .ant-card-body {
    padding: 16px 20px 24px 20px;
  }
`;

const overviewContentStyle = css`
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #ffffff;
`;

const overviewTopRowStyle = css`
  display: grid;
  grid-template-columns: minmax(0, 571fr) minmax(0, 539fr);
  aspect-ratio: 2048 / 806;
  min-height: 440px;

  column-gap: 2%;

  @container (max-width: 800px) {
    grid-template-columns: minmax(0, 1fr);
    aspect-ratio: auto;
    gap: 16px;

    > .ant-card {
      min-height: 440px;
    }
  }
`;

const overviewTrendRowStyle = css`
  aspect-ratio: 2048 / 604;
  min-height: 340px;
`;

const overviewHeaderStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const overviewFilterRowStyle = css`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-height: 44px;

  .ant-space {
    max-width: 100%;
    justify-content: flex-end;
  }

  .ant-space-item,
  .ant-picker {
    max-width: 100%;
  }

  .bold-select-value .ant-select-selection-item {
    font-weight: 600 !important;
    color: #000000 !important;
  }
`;
